import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

const CONSENT_KEY = "gdpr-consent";
const CONSENT_VERSION = "v1.0";

export const ConsentBanner = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const checkConsent = async () => {
      // For non-authenticated users, check localStorage
      if (!user) {
        const consent = localStorage.getItem(CONSENT_KEY);
        if (!consent) {
          setShowBanner(true);
        }
        return;
      }

      // For authenticated users, check database
      const { data, error } = await supabase
        .from('user_consent')
        .select('*')
        .eq('user_id', user.id)
        .eq('consent_version', CONSENT_VERSION)
        .single();

      if (error || !data) {
        // No consent record found
        const localConsent = localStorage.getItem(CONSENT_KEY);
        if (!localConsent) {
          setShowBanner(true);
        }
      }
    };

    checkConsent();
  }, [user]);

  const handleAccept = async () => {
    const consentData = {
      analytics: true,
      functional: true,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));

    // If user is authenticated, also save to database
    if (user) {
      await supabase.from('user_consent').upsert({
        user_id: user.id,
        consent_version: CONSENT_VERSION,
        analytics_consent: true,
        functional_consent: true,
        marketing_consent: false,
        user_agent: navigator.userAgent,
      });
    }

    setShowBanner(false);
  };

  const handleReject = async () => {
    const consentData = {
      analytics: false,
      functional: true,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));

    // If user is authenticated, also save to database
    if (user) {
      await supabase.from('user_consent').upsert({
        user_id: user.id,
        consent_version: CONSENT_VERSION,
        analytics_consent: false,
        functional_consent: true,
        marketing_consent: false,
        user_agent: navigator.userAgent,
      });
    }

    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom">
      <Card className="max-w-4xl mx-auto p-6 shadow-lg border-2">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-2">{t('consent.cookie.title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('consent.cookie.bodyPrefix')}{" "}
              <Link to="/privacy" className="underline hover:text-primary">
                {t('consent.cookie.privacyPolicy')}
              </Link>.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleAccept} size="sm">
                {t('consent.cookie.acceptAll')}
              </Button>
              <Button onClick={handleReject} variant="outline" size="sm">
                {t('consent.cookie.essentialOnly')}
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/privacy">{t('consent.cookie.learnMore')}</Link>
              </Button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleReject}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
};
